import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ImageCardDetails from '../ImageCardDetails/ImageCardDetails';
import SearchPicture from '../SearchPicture/SearchPicture';
import { defaultFilters } from '../Filters/Filters';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useDebounce from '../../hooks/useDebounce';
import useInfinitePixabay from '../../hooks/useInfinitePixabay';
import { SkeletonGrid } from '../Skeleton/SkeletonCard';
import LoadMoreSentinel from '../InfiniteScroll/LoadMoreSentinel';

const ImageCard = ({
    imageType = 'photo',
    label = 'photos',
    words = [label],
}) => {
    const location = useLocation();
    const [query, setQuery] = useState(() => new URLSearchParams(location.search).get('q') || '');
    const [filters, setFilters] = useState(defaultFilters);

    // Sync the search box when arriving via a category link (?q=...).
    useEffect(() => {
        setQuery(new URLSearchParams(location.search).get('q') || '');
    }, [location.search]);

    const term = useDebounce(query, 500);

    const params = useMemo(() => ({
        q: term,
        image_type: imageType,
        orientation: filters.orientation,
        order: filters.order,
        safesearch: String(filters.safesearch),
        min_width: filters.minWidth || '',
        colors: filters.colors || '',
    }), [term, imageType, filters]);

    const {
        items: images,
        total,
        loading: Loading,
        loadingMore,
        error,
        hasMore,
        loadMore,
    } = useInfinitePixabay('https://pixabay.com/api/', params);

    return (
       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
           <SearchPicture
                value={query}
                onChange={setQuery}
                filters={filters}
                setFilters={setFilters}
                words={words}
           ></SearchPicture>

           {!Loading && !error && images.length > 0 &&
                <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-6">
                    {total.toLocaleString()} Free {label}
                    {term && <span className="capitalize"> of {term}</span>}
                </h2>
           }

           {Loading && <SkeletonGrid count={6} />}

           {!Loading && error &&
                <div className="flex flex-col items-center justify-center text-center mt-16 text-rose-400">
                    <FontAwesomeIcon icon={faImage} className="text-6xl mb-4" />
                    <h2 className="text-2xl font-bold">Something went wrong</h2>
                    <p className="mt-1">{error}</p>
                </div>
           }

           {!Loading && !error && images.length === 0 &&
                <div className="flex flex-col items-center justify-center text-center mt-16 text-gray-400 dark:text-gray-500">
                    <FontAwesomeIcon icon={faImage} className="text-6xl mb-4" />
                    <h2 className="text-2xl font-bold">No images found</h2>
                    <p className="mt-1">Try searching for something else.</p>
                </div>
           }

           {!Loading && images.length > 0 &&
               <>
                   <div className="grid sm:grid-cols-2 md:grid-cols-3 grid-cols-1 gap-6 animate-fade-in">
                       {
                           images.map(image =><ImageCardDetails key={image.id} image={image}></ImageCardDetails>)
                       }
                   </div>
                   <LoadMoreSentinel onLoadMore={loadMore} hasMore={hasMore} loadingMore={loadingMore} />
               </>
           }
       </div>
    );
};

export default ImageCard;
